import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, memo } from 'react';
import { cx } from '@emotion/css';

import TnCDialog from 'components/dialogs/TnCDialog';
import useGlobalState from 'actions';
import { Button, Checkbox, useSnackbar } from '@new-components/elements';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import {
  FormParam,
  SavedApplicationDataResponse,
  useRGetSavedApplication,
  useRSubmitApplication,
} from '@repositories/new-application';
import { excludeKey } from '@utils/object';

import ApplicationCard from '../../application-card';
import {
  SectionDomicile,
  SectionIdentityPhoto,
  SectionPersonalContact,
  SectionPersonalData,
} from '../personal-identity';
import {
  SectionBiologicalFamily,
  SectionCloseFamily,
} from '../family-information';
import { SectionAccountInformation, SectionExpenseIncome } from '../financial';

import useTncDialogHandler from './usecase/use-tnc-dialog-handler';
import { defaultValues, reviewSchema } from './constants';
import { reviewCx } from './styles';

const Review = () => {
  const [, actions] = useGlobalState();
  const { onOpen } = useSnackbar();

  const form = useForm<FormParam<'review'>>({
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(reviewSchema),
  });

  const { handleSubmit, setValue, reset } = form;

  const { mutate } = useRSubmitApplication({
    onMutate: () => {
      actions.openLoadingOverlay();
    },
    onSettled: () => {
      actions.closeLoadingOverlay();
    },
    onSuccess: () => {
      window.location.replace('/home');
    },
    onError: (error) => {
      const statusCode = error.payload?.statusCode;
      const errorMessage =
        (error.payload?.errors as Array<string>)[0] ??
        'Oops..., something went wrong';

      switch (statusCode) {
        case 400:
          onOpen({ message: errorMessage, severity: 'error' });
          break;

        default:
          onOpen({ message: errorMessage, severity: 'error' });
          break;
      }
    },
  });
  const {
    onOpen: onTncDialogOpen,
    isOpen: isTncDialogOpen,
    onRejectTnc,
    onAcceptTnc,
  } = useTncDialogHandler({ onReject: () => setValue('privacyPolicy', false) });

  function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      onTncDialogOpen();
    }
  }

  function onSubmit(data: FormParam<'review'>) {
    mutate({
      variables: excludeKey<SavedApplicationDataResponse<'review'>>(
        data as SavedApplicationDataResponse<'review'>,
        ['privacyPolicy', 'validData', 'ktp', 'selfieKtp', 'currentStep'],
      ),
    });
  }

  useRGetSavedApplication<SavedApplicationDataResponse<'review'>>({
    step: 4,
    onSuccess: ({ data }) => {
      reset(data);
    },
  });

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cx(reviewCx, 'review')}
      >
        <SectionIdentityPhoto readonly />
        <SectionPersonalData readonly />
        <SectionDomicile readonly />
        <SectionPersonalContact readonly />
        <SectionCloseFamily readonly />
        <SectionBiologicalFamily readonly />
        <SectionExpenseIncome readonly />
        <SectionAccountInformation readonly />

        <div className='agreement'>
          <Checkbox
            onChange={handleCheckboxChange}
            name='privacyPolicy'
            label='Saya telah membaca dan menyentujui Syarat & Ketentuan dan Kebijakan Privasi JULO'
          />
          <Checkbox
            name='validData'
            label='Saya setuju dengan pemeriksaan dan validasi data pribadi saya.'
          />
        </div>

        <ApplicationCard>
          <Button type='submit' fullWidth>
            Kirim Formulir
          </Button>
        </ApplicationCard>
      </form>
      <TnCDialog
        open={isTncDialogOpen}
        onAcceptTnc={onAcceptTnc}
        onRejectTnc={onRejectTnc}
      />
    </FormProvider>
  );
};

export default memo(Review);
